// Top-anchored hint toast. Standalone primitive, not coupled to anything
// else in the codebase. Slides down from the top of the viewport, holds for
// ~2 seconds, slides back up. Tap to dismiss early.
//
// DOM lookup is internal and lazy: if #hint-toast-root is missing (e.g.
// stale HTML cache), the call silently no-ops with a console.warn — the
// rest of the app keeps working.

const FALLBACK_REMOVE_MS = 4500

let cachedRoot: HTMLElement | null = null
let rootMissingWarned = false

function getRoot(): HTMLElement | null {
  if (cachedRoot !== null) return cachedRoot
  const el = document.getElementById('hint-toast-root')
  if (el === null) {
    if (!rootMissingWarned) {
      rootMissingWarned = true
      console.warn('hint-toast-root not in DOM; hint toasts disabled')
    }
    return null
  }
  cachedRoot = el
  return el
}

export interface ShowHintToastOptions {
  readonly variant?: 'orange' | 'black'
}

export function showHintToast(message: string, options?: ShowHintToastOptions): void {
  const root = getRoot()
  if (root === null) return

  root.replaceChildren()
  const el = document.createElement('div')
  const classes = ['hint-toast']
  if (options?.variant === 'orange') classes.push('hint-toast--orange')
  if (options?.variant === 'black') classes.push('hint-toast--black')
  el.className = classes.join(' ')
  el.textContent = message

  let removed = false
  const remove = (): void => {
    if (removed) return
    removed = true
    if (el.parentElement === root) {
      root.replaceChildren()
    }
  }

  el.addEventListener('animationend', remove, { once: true })
  el.addEventListener('click', remove)
  setTimeout(remove, FALLBACK_REMOVE_MS)

  root.appendChild(el)
}
